import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { BusinessError } from '@/api/envelope';
import { useAuth } from '@/contexts';
import {
  fetchContarCteEmalote,
  fetchContarNfcEmalote,
  fetchContarNfeEmalote,
} from '../api/ContarDocumentosEmaloteApi';
import type {
  ContarCteEmalotePayload,
  ContarNfcEmalotePayload,
  ContarNfeEmalotePayload,
} from '../types';

/*
  Estes hooks não avisam erro por toast. A tela chama quatro contagens a cada busca (NF-e de
  entrada e de saída, CT-e e NFC-e), e um toast por contagem que falhava empilhava avisos
  repetidos a cada busca (LC-11). Quem avisa é o selo E-Malote de cada modelo, que fica com o
  erro enquanto ele durar (`features/documentos/components/SeloEmalote.tsx`).
*/

type OpcoesDaContagem = Omit<UseQueryOptions<number, Error>, 'queryKey' | 'queryFn'>;

/*
  A resposta do servidor sem a contagem vira `BusinessError` (`ContarDocumentosEmaloteApi.ts`): a
  recusa em HTTP 200, como "Contribuinte sem caixa de entrada ativa", ou o sucesso sem o número.
  Repetir a chamada dá a mesma resposta. Com o `retry: 1` do app (`src/lib/query-client.ts`), ela
  saía duas vezes e só aparecia 1 s depois. As outras falhas seguem a regra do cliente.
*/
function useRepeticaoDaContagem(): OpcoesDaContagem['retry'] {
  const regraDoCliente = useQueryClient().getDefaultOptions().queries?.retry;
  return (falhas, erro) => {
    if (erro instanceof BusinessError) return false;
    if (typeof regraDoCliente === 'function') return regraDoCliente(falhas, erro);
    if (typeof regraDoCliente === 'number') return falhas < regraDoCliente;
    // Cliente sem regra: vale a do React Query no navegador, que repete 3 vezes.
    return regraDoCliente ?? falhas < 3;
  };
}

export function useContarNfeEmalote(payload?: ContarNfeEmalotePayload, options?: OpcoesDaContagem) {
  const { user } = useAuth();
  const cnpjGestor = user?.cnpj;
  const retry = useRepeticaoDaContagem();

  const enabledPayload =
    !!payload?.CNPJ_Contribuinte &&
    !!payload?.DataInicio &&
    !!payload?.DataFim &&
    !!payload?.Ind_Oper;

  return useQuery<number, Error>({
    queryKey: ['documentos-emalote-contar-nfe', cnpjGestor, payload],
    queryFn: () => fetchContarNfeEmalote(payload as ContarNfeEmalotePayload),
    staleTime: 0,
    retry,
    ...options,
    enabled: (options?.enabled ?? true) && !!cnpjGestor && enabledPayload,
  });
}

export function useContarNfcEmalote(payload?: ContarNfcEmalotePayload, options?: OpcoesDaContagem) {
  const { user } = useAuth();
  const cnpjGestor = user?.cnpj;
  const retry = useRepeticaoDaContagem();

  const enabledPayload =
    !!payload?.CNPJ_Contribuinte && !!payload?.DataInicio && !!payload?.DataFim;

  return useQuery<number, Error>({
    queryKey: ['documentos-emalote-contar-nfc', cnpjGestor, payload],
    queryFn: () => fetchContarNfcEmalote(payload as ContarNfcEmalotePayload),
    staleTime: 0,
    retry,
    ...options,
    enabled: (options?.enabled ?? true) && !!cnpjGestor && enabledPayload,
  });
}

export function useContarCteEmalote(payload?: ContarCteEmalotePayload, options?: OpcoesDaContagem) {
  const { user } = useAuth();
  const cnpjGestor = user?.cnpj;
  const retry = useRepeticaoDaContagem();

  const enabledPayload =
    !!payload?.CNPJ_Contribuinte && !!payload?.DataInicio && !!payload?.DataFim;

  return useQuery<number, Error>({
    queryKey: ['documentos-emalote-contar-cte', cnpjGestor, payload],
    queryFn: () => fetchContarCteEmalote(payload as ContarCteEmalotePayload),
    staleTime: 0,
    retry,
    ...options,
    enabled: (options?.enabled ?? true) && !!cnpjGestor && enabledPayload,
  });
}
